/**
 * @file AbaProviderWaitlist.jsx
 * @description Standalone SEO/conversion page at /aba-provider-waitlist — the
 *   honest, no-inventory provider-conversion page. ABAcases does not yet have
 *   a live, browsable inventory of open cases, so this page never claims one.
 *   Instead it invites RBTs, BTs, BCBAs, BCaBAs, and ABA therapists to create a
 *   free profile, get notified as local case opportunities open, and use the
 *   free tracker in the meantime. No fake counts, no JobPosting schema, no
 *   competitor names. Mirrors the structure of AboutAbaCases.jsx.
 * @module components/AbaProviderWaitlist
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, DollarIcon, CalendarIcon, ShieldIcon, BriefcaseIcon,
  UsersIcon, BellIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import { MoreDetails } from "./Disclosure";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function AbaProviderWaitlist({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "Join ABA Case Alerts for RBTs, BCBAs, and ABA Therapists | ABAcases",
    description: "RBTs, BTs, BCBAs, BCaBAs, and ABA therapists can join ABAcases to get notified as local case opportunities open in their area.",
    canonical: "/aba-provider-waitlist",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-aba-provider-waitlist", [
    { name: "Home", url: "/" },
    { name: "ABA Case Finder", url: "/aba-case-finder" },
    { name: "Join ABA Case Alerts", url: "/aba-provider-waitlist" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goJoin = () => {
    track(A.CASE_ALERT_CLICK, {
      supabaseMeta: { page: "/aba-provider-waitlist/" },
      ga4Params: ctaMeta({
        pagePath: "/aba-provider-waitlist/",
        pageType: "provider",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "provider",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('abaProviderWaitlist.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('abaProviderWaitlist.breadcrumbCaseFinder'), onClick: () => onNavigate("aba-case-finder") },
            { label: t('abaProviderWaitlist.breadcrumbJoinAlerts') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('abaProviderWaitlist.heroBadge')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>{t('abaProviderWaitlist.heroDomainLabel')}</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('abaProviderWaitlist.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('abaProviderWaitlist.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t('abaProviderWaitlist.heroBody')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: T.shadowIndigo }}>
              <BellIcon size={16} color="#fff" /> {t('abaProviderWaitlist.ctaJoinFree')}
            </button>
          </div>
          <p style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#cbd5e1", lineHeight: 1.55, maxWidth: 560, margin: "18px 0 0" }}>
            <ShieldIcon size={15} color={INDIGO_LT} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{t('providerPages.npiTrustLine')}</span>
          </p>
        </div>
      </header>

      {/* ── 1. Join local ABA case alerts ─────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaProviderWaitlist.section1Title')}
          </h2>
          <MoreDetails
            text={t('abaProviderWaitlist.section1Body1')}
            max={170}
            style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}
            extra={<p style={{ ...{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }, margin: "10px 0 0" }}>{t('abaProviderWaitlist.section1Body2')}</p>}
          />
        </div>
      </section>

      {/* ── 2. Built for RBTs, BTs, BCBAs, BCaBAs, and ABA therapists ── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('abaProviderWaitlist.section2Title')}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
            {[
              { Icon: UsersIcon, title: t('abaProviderWaitlist.audienceRbtBtTitle'), body: t('abaProviderWaitlist.audienceRbtBtBody') },
              { Icon: ShieldIcon, title: t('abaProviderWaitlist.audienceBcbaTitle'), body: t('abaProviderWaitlist.audienceBcbaBody') },
              { Icon: BriefcaseIcon, title: t('abaProviderWaitlist.audienceBroadTitle'), body: t('abaProviderWaitlist.audienceBroadBody') },
              { Icon: BellIcon, title: t('abaProviderWaitlist.audienceProfileTitle'), body: t('abaProviderWaitlist.audienceProfileBody') },
            ].map((c) => (
              <div key={c.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{c.title}</div>
                  <MoreDetails text={c.body} style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Tell agencies what kind of cases fit you ───────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaProviderWaitlist.section3Title')}
          </h2>
          <div style={{ marginBottom: 16 }}>
            <MoreDetails text={t('abaProviderWaitlist.section3Body1')} max={170} style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 18 }}>
            {[
              { Icon: ShieldIcon, title: t('abaProviderWaitlist.criteriaRoleTitle'), body: t('abaProviderWaitlist.criteriaRoleBody') },
              { Icon: MapPinIcon, title: t('abaProviderWaitlist.criteriaLocationTitle'), body: t('abaProviderWaitlist.criteriaLocationBody') },
              { Icon: CalendarIcon, title: t('abaProviderWaitlist.criteriaScheduleTitle'), body: t('abaProviderWaitlist.criteriaScheduleBody') },
              { Icon: DollarIcon, title: t('abaProviderWaitlist.criteriaPayTitle'), body: t('abaProviderWaitlist.criteriaPayBody') },
            ].map((c) => (
              <div key={c.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={18} color={INDIGO} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{c.title}</div>
                  <MoreDetails text={c.body} style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }} />
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.65 }}>
            {t('abaProviderWaitlist.section3Body2Pre')}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaProviderWaitlist.linkCaseFinderProfile')}</SeoLink>{" "}
            {t('abaProviderWaitlist.section3Body2Post')}
          </p>
        </div>
      </section>

      {/* ── 4. Track hours and payments while you work ────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaProviderWaitlist.section4Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, margin: "0 0 8px" }}>
            {t('abaProviderWaitlist.section4Body1Pre')}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaProviderWaitlist.linkTimesheetTracker')}</SeoLink>{t('abaProviderWaitlist.section4Body1Post')}
          </p>
          <MoreDetails text="" extra={<p style={{ ...{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }, margin: "6px 0 0" }}>{t('abaProviderWaitlist.section4Body2')}</p>} />
        </div>
      </section>

      {/* ── 5. Where we work ──────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('abaProviderWaitlist.section5Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, margin: "0 0 8px" }}>
            {t('abaProviderWaitlist.section5Body1Pre')}{" "}
            <SeoLink to="rbt-jobs-south-florida" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaProviderWaitlist.linkMiamiSouthFlorida')}</SeoLink>{" "}{t('abaProviderWaitlist.section5Body1Mid')}{" "}
            <SeoLink to="rbt-jobs-north-carolina" style={{ color: INDIGO, fontWeight: 700 }}>{t('abaProviderWaitlist.linkCharlotteNorthCarolina')}</SeoLink>{t('abaProviderWaitlist.section5Body1Post')}
          </p>
        </div>
      </section>

      {/* One quiet ending instead of a second CTA band: the hero is this page's only call to action. */}
      <QuietEnding isMobile={isMobile} />

      <PublicFooter />
    </div>
  );
}
